export const GROUP_ROLES = {
  SUPER_ADMIN: "group_super_admin",
  ADMIN: "group_admin",
  COLLABORATOR: "group_collaborator",
  NO_PERMISSIONS: "no_permissions",
};

export const normalizeGroupRole = (role) => {
  const normalized = (role || "").toLowerCase();
  if (Object.values(GROUP_ROLES).includes(normalized)) return normalized;
  if (normalized === "no permissions") return GROUP_ROLES.NO_PERMISSIONS;
  return GROUP_ROLES.COLLABORATOR;
};

export const hasMemberOrganisationAccess = (userData) => {
  const roles = Array.isArray(userData?.role) ? userData.role : [userData?.role || ""];
  const hasAccessRole = roles.some(r => ["teacher", "school", "schooladmin"].includes((r || "").toLowerCase()));

  if (hasAccessRole) {
    return true;
  }

  return Boolean(userData?.schoolId?._id || userData?.schoolId || userData?.schoolName);
};

export const getActiveGroup = (userData) => {
  if (Array.isArray(userData?.organisationGroupMemberships) && userData.organisationGroupMemberships.length > 0) {
    return userData.organisationGroupMemberships[0].organisationGroupId;
  }
  return userData?.organisationGroupId;
};

export const getActiveGroupRole = (userData) => {
  if (Array.isArray(userData?.organisationGroupMemberships) && userData.organisationGroupMemberships.length > 0) {
    return userData.organisationGroupMemberships[0].groupRole;
  }

  const explicitRole = userData?.organisationGroupRole || userData?.groupRole || userData?.organisationRole;
  if (explicitRole) return explicitRole;

  if (Array.isArray(userData?.role)) {
    const foundGroupRole = userData.role.find(r =>
      Object.values(GROUP_ROLES).includes((r || "").toLowerCase()) || (r || "").toLowerCase() === "no permissions"
    );
    if (foundGroupRole) return foundGroupRole;
  }

  return null;
};

export const getOrganisationGroupRole = (userData) => {
  const groupData = getActiveGroup(userData);
  const userId = userData?._id;
  const explicitRole = getActiveGroupRole(userData);

  if (explicitRole) {
    return normalizeGroupRole(explicitRole);
  }

  if (groupData?.superAdminId && userId && groupData.superAdminId === userId) {
    return GROUP_ROLES.SUPER_ADMIN;
  }

  if (Array.isArray(groupData?.admins) && userId && groupData.admins.includes(userId)) {
    return GROUP_ROLES.ADMIN;
  }

  if (
    Array.isArray(groupData?.collaborators) &&
    userId &&
    groupData.collaborators.includes(userId)
  ) {
    return GROUP_ROLES.COLLABORATOR;
  }

  return GROUP_ROLES.COLLABORATOR;
};

export const formatOrganisationRole = (role) => {
  switch (normalizeGroupRole(role)) {
    case GROUP_ROLES.SUPER_ADMIN:
      return "Group Super Admin";
    case GROUP_ROLES.ADMIN:
      return "Group Admin";
    case GROUP_ROLES.NO_PERMISSIONS:
      return "No Permissions";
    default:
      return "Group Collaborator";
  }
};

export const isOrganisationGroupUser = (userData) => {
  const group = getActiveGroup(userData);
  return Boolean(group && (typeof group === 'string' || group._id));
};

export const getOrganisationRoleAccess = (userDataOrRole, schoolData = null) => {
  const hasMemberAccess =
    typeof userDataOrRole === "string"
      ? false
      : hasMemberOrganisationAccess(userDataOrRole);

  const isGroupUser =
    typeof userDataOrRole === "string"
      ? false
      : isOrganisationGroupUser(userDataOrRole);

  const normalizedGroupRole =
    typeof userDataOrRole === "string"
      ? normalizeGroupRole(userDataOrRole)
      : getOrganisationGroupRole(userDataOrRole);

  const memberRoles =
    typeof userDataOrRole === "string"
      ? []
      : (Array.isArray(userDataOrRole?.role) ? userDataOrRole.role : [userDataOrRole?.role || ""]).map(r => (r || "").toLowerCase());

  const isSuperAdmin = normalizedGroupRole === GROUP_ROLES.SUPER_ADMIN;
  const isAdmin = normalizedGroupRole === GROUP_ROLES.ADMIN;
  const isCollaborator = normalizedGroupRole === GROUP_ROLES.COLLABORATOR;
  const hasNoPermissions = normalizedGroupRole === GROUP_ROLES.NO_PERMISSIONS;

  // Standard Member Role Checks
  const isSchool = memberRoles.includes("school");
  const isSchoolAdmin = memberRoles.includes("schooladmin");
  const isTeacher = memberRoles.includes("teacher");

  // Feature flags from schoolData and userData
  const isLearnerAccessible = Boolean(
    userDataOrRole?.learnerAccessible ||
    schoolData?.learnerAccessible ||
    schoolData?.schoolId?.learnerAccessible
  );
  const permissionToManageAdmins = Boolean(schoolData?.permissionToManageAdmins || userDataOrRole?.permissionToManageAdmins);

  return {
    // Group Role Access
    role: normalizedGroupRole,
    roleLabel: formatOrganisationRole(normalizedGroupRole),
    isGroupUser,
    isSuperAdmin,
    isAdmin,
    isCollaborator,
    hasNoPermissions,

    // Group Features
    canViewGroupDashboard: isGroupUser && !hasNoPermissions,
    canViewStaff: isGroupUser && (isSuperAdmin || isAdmin || isSchoolAdmin || isSchool),
    canViewTeacherDetail: isGroupUser && !hasNoPermissions,
    canViewMemberOrganisations: isGroupUser && (isSuperAdmin || isAdmin || isCollaborator),
    canInviteMemberOrganisation: isGroupUser && isSuperAdmin,
    canEditPermissions: isGroupUser && (isSuperAdmin || isAdmin),
    canRemoveMemberOrganisation: isGroupUser && isSuperAdmin,
    canManageGroupAdminPermissions: isGroupUser && isSuperAdmin,
    canViewGroupSchoolDetails: isGroupUser && hasMemberAccess,

    // Standard Member Access
    hasMemberAccess,
    isSchool,
    isSchoolAdmin,
    isTeacher,

    // Standard Member Features
    canViewMemberDashboard: hasMemberAccess,
    canViewBrowse: hasMemberAccess,
    canViewCollections: hasMemberAccess,
    canViewStrategyPacks: hasMemberAccess,
    canViewLearners: hasMemberAccess && (isLearnerAccessible || isSchoolAdmin || isSchool || (isTeacher && isSuperAdmin)),
    canViewParents: hasMemberAccess,
    canViewClasses: hasMemberAccess && (isLearnerAccessible || isSchoolAdmin || isSchool || (isTeacher && isSuperAdmin)),
    canViewTeachers: hasMemberAccess && (isSchoolAdmin || isSchool || !isTeacher),
    canManageAdmin: hasMemberAccess && (isSchool || (isSchoolAdmin && permissionToManageAdmins)),
    canViewGroupInvites: hasMemberAccess && isSchool,
    canViewResourceMapping: hasMemberAccess,
    canViewHelpSupport: hasMemberAccess || isGroupUser,
    canViewSettings: hasMemberAccess || isGroupUser,

  };
};
